<template>
  <div class="oc-section-picker">
    <div class="oc-section-picker__header">
      <feather-icon icon="LayersIcon" size="16" class="text-primary mr-50" />
      <div>
        <h6 class="oc-section-picker__title mb-0">Document sections</h6>
        <p class="text-muted small oc-section-picker__lead mb-0">
          Toggle sections on or off, then open each to edit. Hidden sections are omitted from the PDF.
        </p>
      </div>
    </div>

    <b-row class="oc-section-picker__grid">
      <b-col
        v-for="section in sections"
        :key="section.key"
        cols="12"
        sm="6"
        lg="4"
        class="oc-section-picker__col"
      >
        <div
          class="oc-section-picker__item border-0 shadow-none h-100"
          :class="{
            'oc-section-picker__item--on': section.included,
            'oc-section-picker__item--off': !section.included,
          }"
        >
          <div class="oc-section-picker__item-top">
            <b-badge
              :variant="section.included ? 'primary' : 'light-secondary'"
              class="oc-section-picker__badge"
            >
              §{{ section.number }}
            </b-badge>
            <b-form-checkbox
              switch
              :checked="section.included"
              :disabled="disabled"
              class="oc-section-picker__switch mb-0"
              @change="$emit('toggle', section.key)"
            />
          </div>
          <div class="oc-section-picker__label">{{ section.label }}</div>
          <small class="text-muted d-block oc-section-picker__hint">{{ section.hint }}</small>
          <b-button
            v-if="section.included"
            size="sm"
            variant="outline-primary"
            class="oc-section-picker__open-btn mt-50"
            :disabled="disabled"
            block
            @click="$emit('select', section.key)"
          >
            <feather-icon icon="Edit3Icon" size="12" class="mr-25" />
            {{ section.isSalaryStep ? 'Open table' : 'Edit section' }}
          </b-button>
          <div v-else class="oc-section-picker__off mt-50">
            <feather-icon icon="EyeOffIcon" size="12" class="mr-25" />
            Not included
          </div>
        </div>
      </b-col>
    </b-row>

    <div class="oc-section-picker__footer">
      <span class="oc-section-picker__count">
        <strong>{{ includedCount }}</strong> of {{ sections.length }} sections included
      </span>
      <b-button
        size="sm"
        variant="outline-secondary"
        class="oc-section-picker__btn"
        :disabled="disabled || includedCount === sections.length"
        @click="$emit('show-all')"
      >
        <feather-icon icon="CheckSquareIcon" size="13" class="mr-25" />
        Include all
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BBadge,
  BButton,
  BFormCheckbox,
} from 'bootstrap-vue'

export default {
  name: 'OfferSectionPicker',
  components: {
    BRow,
    BCol,
    BBadge,
    BButton,
    BFormCheckbox,
  },
  props: {
    sections: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    includedCount() {
      return this.sections.filter(s => s.included).length
    },
  },
}
</script>

<style scoped>
.oc-section-picker {
  background: #f8f9fb;
  border: 1px solid #eef0f3;
  border-radius: 0.5rem;
  padding: 0.85rem 1rem;
}

.oc-section-picker__header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.oc-section-picker__title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #2c2c2c;
}

.oc-section-picker__lead {
  line-height: 1.35;
  margin-top: 0.15rem;
}

.oc-section-picker__col {
  padding-left: 0.35rem;
  padding-right: 0.35rem;
  margin-bottom: 0.65rem;
}

.oc-section-picker__item {
  background: #fff;
  border-radius: 0.45rem;
  padding: 0.65rem 0.75rem;
}

.oc-section-picker__item--off {
  opacity: 0.75;
  background: #fafbfc;
}

.oc-section-picker__item-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.4rem;
}

.oc-section-picker__badge {
  font-size: 0.625rem;
  font-weight: 700;
  padding: 0.2rem 0.45rem;
}

.oc-section-picker__label {
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.25;
  margin-bottom: 0.15rem;
  color: #2c2c2c;
}

.oc-section-picker__hint {
  font-size: 0.6875rem;
  line-height: 1.3;
  min-height: 2.6em;
}

.oc-section-picker__off {
  display: flex;
  align-items: center;
  font-size: 0.6875rem;
  color: #9ca3af;
  font-weight: 500;
}

.oc-section-picker__open-btn {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
}

.oc-section-picker__switch >>> .custom-control-label {
  padding-left: 0;
  padding-right: 2.25rem;
}

.oc-section-picker__switch >>> .custom-control-label::before,
.oc-section-picker__switch >>> .custom-control-label::after {
  left: auto;
  right: 0;
}

.oc-section-picker__footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.25rem;
  padding-top: 0.65rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  gap: 0.5rem;
}

.oc-section-picker__count {
  font-size: 0.8125rem;
  color: #6e6b7b;
}

.oc-section-picker__btn {
  font-size: 0.8125rem;
  font-weight: 600;
}

.oc-section-picker__grid {
  margin-left: -0.35rem;
  margin-right: -0.35rem;
}
</style>
